/* AdminScanVisits — iter97.88
 *
 * Operations queue for the Invisique IO Scanning Service. Shows every
 * scan visit doctors have requested across the platform with two tabs:
 *
 *   • Unassigned (status=scheduled) — pick a technician for each visit
 *   • Assigned   (status=assigned)  — in-flight visits awaiting upload
 *
 * The "Assign technician" action calls POST /admin/scan-visits/{id}/assign
 * and flips the row out of the Unassigned tab.
 *
 * A3 will add: assignment notification → technician portal → scan upload
 *               → status=completed → doctor resume-from-visit signal.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    Calendar, ClockClockwise, User, Stethoscope, MapPin, ClipboardText,
    UserPlus, CheckCircle, ArrowSquareOut, WarningCircle, X,
} from "@phosphor-icons/react";
import BackToBreadcrumb from "@/components/admin/BackToBreadcrumb";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";

// iter101.31 — Unified with /admin/patients + /admin/cases grammar. Uses
// the FilterChipRow with 5 real statuses (backend supports scheduled /
// assigned / completed / cancelled). "Unassigned" is just the friendlier
// label for `scheduled` (the raw status value planners use).
const BUCKETS = [
    { id: "unassigned", label: "Unassigned", status: "scheduled",  tone: "amber"   },
    { id: "assigned",   label: "Assigned",   status: "assigned",   tone: "primary" },
    { id: "completed",  label: "Completed",  status: "completed",  tone: "emerald" },
    { id: "cancelled",  label: "Cancelled",  status: "cancelled",  tone: "rose"    },
];

function relativeDateLabel(dateStr) {
    // Soft cue for prioritization. "Today", "Tomorrow", "in 3 days", "5 days ago".
    if (!dateStr) return "";
    try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const d = new Date(dateStr + "T00:00:00");
        const diffDays = Math.round((d - today) / (24 * 60 * 60 * 1000));
        if (diffDays === 0) return "Today";
        if (diffDays === 1) return "Tomorrow";
        if (diffDays === -1) return "Yesterday";
        if (diffDays > 1)  return `in ${diffDays} days`;
        return `${Math.abs(diffDays)} days ago`;
    } catch { return ""; }
}
function relativeTone(dateStr) {
    if (!dateStr) return "text-muted-foreground";
    try {
        const today = new Date(); today.setHours(0,0,0,0);
        const d = new Date(dateStr + "T00:00:00");
        const diff = Math.round((d - today) / (24 * 60 * 60 * 1000));
        if (diff < 0) return "text-rose-700";   // overdue
        if (diff === 0) return "text-amber-700"; // today
        if (diff === 1) return "text-amber-600"; // tomorrow
        return "text-muted-foreground";
    } catch { return "text-muted-foreground"; }
}

export default function AdminScanVisits() {
    const [tab, setTab] = useState("unassigned");
    const [items, setItems] = useState([]);
    const [techs, setTechs] = useState([]);
    const [techsLoaded, setTechsLoaded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [counts, setCounts] = useState({ unassigned: 0, assigned: 0, completed: 0, cancelled: 0 });

    // Assign dialog state — single source of truth at page level so the
    // table row only has to fire "open dialog for this visit".
    const [assignFor, setAssignFor] = useState(null); // visit row
    const [pickedTech, setPickedTech] = useState("");
    const [assigning, setAssigning] = useState(false);

    const activeBucket = BUCKETS.find((b) => b.id === tab) || BUCKETS[0];

    const loadForTab = (bucketId) => {
        const meta = BUCKETS.find((b) => b.id === bucketId) || BUCKETS[0];
        setLoading(true);
        api.get("/admin/scan-visits", { params: { status: meta.status } })
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };
    const loadCounts = () => {
        // iter101.31 — Fetch counts for all 4 real statuses in parallel
        // so the KPI strip + FilterChipRow always show accurate numbers.
        Promise.all(BUCKETS.map((b) =>
            api.get("/admin/scan-visits", { params: { status: b.status } })
                .then((r) => ({ id: b.id, n: r.data?.length || 0 }))
                .catch(() => ({ id: b.id, n: 0 }))
        )).then((rows) => {
            const next = {};
            rows.forEach((r) => { next[r.id] = r.n; });
            setCounts(next);
        });
    };
    useEffect(() => {
        api.get("/admin/technicians")
            .then((r) => setTechs(r.data || []))
            .catch(() => setTechs([]))
            .finally(() => setTechsLoaded(true));
        loadCounts();
    }, []);
    useEffect(() => { loadForTab(tab); }, [tab]);

    const filtered = useMemo(() => {
        if (!q) return items;
        const l = q.toLowerCase();
        return items.filter((v) =>
            (v.patient_name || "").toLowerCase().includes(l) ||
            (v.doctor_name || "").toLowerCase().includes(l) ||
            (v.doctor_clinic_name || "").toLowerCase().includes(l) ||
            (v.patient_phone || "").includes(q) ||
            (v.case_code || "").toLowerCase().includes(l) ||
            (v.assigned_technician_name || "").toLowerCase().includes(l),
        );
    }, [items, q]);

    const openAssign = (visit) => {
        setAssignFor(visit);
        setPickedTech(visit.assigned_technician_id || "");
    };
    const closeAssign = () => {
        if (assigning) return;
        setAssignFor(null);
        setPickedTech("");
    };
    const submitAssign = async () => {
        if (!assignFor) return;
        if (!pickedTech) { toast.error("Pick a technician"); return; }
        setAssigning(true);
        try {
            await api.post(`/admin/scan-visits/${assignFor.id}/assign`, { technician_id: pickedTech });
            const t = techs.find((x) => x.id === pickedTech);
            toast.success(`Assigned to ${t?.name || "technician"}`);
            setAssignFor(null);
            setPickedTech("");
            loadForTab(tab);
            loadCounts();
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" && detail ? detail : "Could not assign — please try again");
        } finally {
            setAssigning(false);
        }
    };

    return (
        <div data-testid="admin-scan-visits-page" className="space-y-6">
            {/* iter100.241 — Back-to-analytics breadcrumb. Renders only when
                the admin arrived via a drill-down. */}
            <BackToBreadcrumb />

            {/* HEADER — iter101.31 · Universal admin page grammar. */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Work · Scan Visits</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Scan Visits</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        Triage and assign technicians for IO Scans by Invisique™ visits booked by doctors.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/admin/technicians">
                        <Button size="sm" className="rounded-full" data-testid="admin-scan-visits-techs-link">
                            <UserPlus size={14} weight="bold" className="mr-1.5" />
                            Manage technicians
                        </Button>
                    </Link>
                </div>
            </div>

            {/* iter101.32 · Empty state — only render AFTER the /admin/technicians
                fetch has resolved. Previously flashed on mount because
                `techs` started as [] and the banner rendered before
                the API responded. */}
            {techsLoaded && techs.length === 0 && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm flex items-start gap-2" data-testid="admin-scan-visits-no-techs-banner">
                    <WarningCircle size={16} weight="fill" className="mt-0.5 shrink-0" />
                    <div>
                        No technicians on file yet. <Link to="/admin/technicians" className="font-semibold underline">Add a technician</Link> before you can assign visits.
                    </div>
                </div>
            )}

            {/* KPI STRIP — Total · Unassigned · Assigned · Completed · Cancelled */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <ScanVisitTile
                    testid="admin-scan-visits-tile-total"
                    label="Total visits"
                    value={(counts.unassigned || 0) + (counts.assigned || 0) + (counts.completed || 0) + (counts.cancelled || 0)}
                    Icon={Calendar} tone="primary"
                    hint="All statuses"
                />
                <ScanVisitTile
                    testid="admin-scan-visits-tile-unassigned"
                    label="Unassigned"
                    value={counts.unassigned || 0}
                    Icon={ClockClockwise} tone="amber"
                    hint="Awaiting technician"
                    onClick={() => setTab("unassigned")}
                    active={tab === "unassigned"}
                />
                <ScanVisitTile
                    testid="admin-scan-visits-tile-assigned"
                    label="Assigned"
                    value={counts.assigned || 0}
                    Icon={UserPlus} tone="primary"
                    hint="In-flight"
                    onClick={() => setTab("assigned")}
                    active={tab === "assigned"}
                />
                <ScanVisitTile
                    testid="admin-scan-visits-tile-completed"
                    label="Completed"
                    value={counts.completed || 0}
                    Icon={CheckCircle} tone="emerald"
                    hint="Scan uploaded"
                    onClick={() => setTab("completed")}
                    active={tab === "completed"}
                />
                <ScanVisitTile
                    testid="admin-scan-visits-tile-cancelled"
                    label="Cancelled"
                    value={counts.cancelled || 0}
                    Icon={X} tone="rose"
                    hint="Doctor stood down"
                    onClick={() => setTab("cancelled")}
                    active={tab === "cancelled"}
                />
            </div>

            {/* FILTER STRIP — Universal grammar. */}
            <div className="flex flex-col gap-3" data-testid="admin-scan-visits-filter-bar">
                <FilterSearchBar
                    query={q}
                    setQuery={setQ}
                    placeholder="Search patient, doctor, clinic, case…"
                    searchTestid="admin-scan-visits-search"
                    testid="admin-scan-visits-filter-search"
                />
                <FilterChipRow
                    chips={BUCKETS.map((b) => ({ id: b.id, label: b.label, count: counts[b.id] || 0, tone: b.tone }))}
                    value={tab}
                    onChange={setTab}
                    testid="admin-scan-visits-chip-row"
                    chipTestidPrefix="admin-scan-visits-pill"
                />
            </div>

            {loading && <div className="text-muted-foreground text-sm text-center py-10">Loading scan visits…</div>}

            {!loading && filtered.length === 0 && (
                <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground" data-testid="admin-scan-visits-empty">
                    <Calendar size={32} className="mx-auto mb-2 opacity-50" />
                    {activeBucket.id === "unassigned" ? "No unassigned scan visits — you're all caught up." :
                     activeBucket.id === "assigned"   ? "No in-flight visits right now." :
                     activeBucket.id === "completed"  ? "No completed visits yet." :
                     "No cancelled visits."}
                </div>
            )}

            {!loading && filtered.length > 0 && (
                <div className="space-y-2">
                    {filtered.map((v) => {
                        const rel = relativeDateLabel(v.date);
                        const relTone = relativeTone(v.date);
                        return (
                            <div
                                key={v.id}
                                className="bg-card border border-border rounded-2xl p-4 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6 hover:border-primary/40 transition-colors"
                                data-testid={`admin-scan-visit-row-${v.id}`}
                            >
                                {/* Date + time block */}
                                <div className="flex items-center gap-3 min-w-[180px]">
                                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex flex-col items-center justify-center text-primary shrink-0">
                                        <Calendar size={18} weight="bold" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-sm">{v.date} · {v.time}</div>
                                        <div className={`text-[11px] font-medium ${relTone}`}>{rel}</div>
                                    </div>
                                </div>

                                {/* Patient */}
                                <div className="flex items-center gap-2 min-w-[200px]">
                                    <User size={14} className="text-muted-foreground shrink-0" />
                                    <div>
                                        <div className="text-sm font-medium">{v.patient_name || "—"}</div>
                                        <div className="text-[11px] text-muted-foreground">{v.patient_phone || ""}</div>
                                    </div>
                                </div>

                                {/* Doctor + clinic */}
                                <div className="flex items-center gap-2 min-w-[220px]">
                                    <Stethoscope size={14} className="text-muted-foreground shrink-0" />
                                    <div>
                                        <div className="text-sm font-medium">{v.doctor_name || "—"}</div>
                                        <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                                            <MapPin size={11} /> {v.doctor_clinic_name || "—"}
                                        </div>
                                    </div>
                                </div>

                                {/* Case link */}
                                <div className="min-w-[140px] text-sm">
                                    {v.case_id ? (
                                        <Link to={`/admin/cases/${v.case_id}`} className="text-primary font-semibold inline-flex items-center gap-1 hover:underline">
                                            {v.case_code || "View case"}
                                            <ArrowSquareOut size={11} />
                                        </Link>
                                    ) : (
                                        <span className="text-xs text-muted-foreground">No case linked</span>
                                    )}
                                </div>

                                {/* Assigned technician — surfaced on the right rail */}
                                {(tab === "assigned" || tab === "completed") && v.assigned_technician_name && (
                                    <div className="flex items-center gap-2 min-w-[160px] text-sm">
                                        <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-xs font-semibold">
                                            {(v.assigned_technician_name || "?").slice(0, 1).toUpperCase()}
                                        </div>
                                        <div>
                                            <div className="font-medium text-xs">{v.assigned_technician_name}</div>
                                            <div className="text-[11px] text-muted-foreground">{v.assigned_technician_phone || ""}</div>
                                        </div>
                                    </div>
                                )}

                                {/* Action: assign / reassign */}
                                <div className="lg:ml-auto flex items-center gap-2">
                                    {tab === "unassigned" ? (
                                        <Button
                                            size="sm"
                                            onClick={() => openAssign(v)}
                                            disabled={techs.length === 0}
                                            data-testid={`admin-scan-visit-assign-btn-${v.id}`}
                                        >
                                            <UserPlus size={13} weight="bold" className="mr-1.5" />
                                            Assign
                                        </Button>
                                    ) : tab === "assigned" ? (
                                        <div className="flex items-center gap-2">
                                            <span className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
                                                <CheckCircle size={11} weight="fill" /> Assigned
                                            </span>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => openAssign(v)}
                                                data-testid={`admin-scan-visit-reassign-btn-${v.id}`}
                                            >
                                                Reassign
                                            </Button>
                                        </div>
                                    ) : tab === "completed" ? (
                                        <span className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
                                            <CheckCircle size={11} weight="fill" /> Completed
                                        </span>
                                    ) : (
                                        <span className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-100 text-rose-700 font-semibold">
                                            <X size={11} weight="bold" /> Cancelled
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Assign dialog */}
            <Dialog open={Boolean(assignFor)} onOpenChange={(o) => !o && closeAssign()}>
                <DialogContent className="max-w-md" data-testid="admin-scan-visits-assign-dialog">
                    <DialogHeader>
                        <DialogTitle>{assignFor?.assigned_technician_id ? "Reassign technician" : "Assign a technician"}</DialogTitle>
                        <DialogDescription>
                            {assignFor ? (
                                <>
                                    {assignFor.patient_name} • {assignFor.date} at {assignFor.time} • {assignFor.doctor_clinic_name}
                                </>
                            ) : null}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 mt-2">
                        <div>
                            <Label className="text-xs font-semibold">Technician</Label>
                            <select
                                value={pickedTech}
                                onChange={(e) => setPickedTech(e.target.value)}
                                className="w-full h-10 mt-1 text-sm rounded-lg border border-border bg-background px-2"
                                data-testid="admin-scan-visits-assign-select"
                            >
                                <option value="">Pick a technician…</option>
                                {techs.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.name} · {t.phone}
                                    </option>
                                ))}
                            </select>
                            {techs.length === 0 && (
                                <p className="text-[11px] text-amber-700 mt-1">
                                    No technicians on file. Add one from /admin/technicians first.
                                </p>
                            )}
                        </div>
                    </div>
                    <DialogFooter className="mt-3">
                        <Button variant="outline" onClick={closeAssign} disabled={assigning}>Cancel</Button>
                        <Button onClick={submitAssign} disabled={assigning || !pickedTech} data-testid="admin-scan-visits-assign-submit">
                            {assigning ? "Assigning…" : "Confirm"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/* iter101.31 — KPI stat tile (mirrors /admin/patients + /admin/cases). */
function ScanVisitTile({ testid, label, value, Icon, tone, hint, onClick, active }) {
    const accent = tone === "emerald" ? "text-emerald-700 bg-emerald-100"
        : tone === "violet" ? "text-violet-700 bg-violet-100"
        : tone === "amber" ? "text-amber-700 bg-amber-100"
        : tone === "rose" ? "text-rose-700 bg-rose-100"
        : "text-primary bg-primary/10";
    const Component = onClick ? "button" : "div";
    return (
        <Component
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`flex items-start gap-3 rounded-xl bg-card border p-3 text-left transition ${
                active ? "border-primary ring-1 ring-primary/30 bg-primary/5"
                       : (onClick ? "border-border hover:border-primary/40 hover:shadow-sm cursor-pointer" : "border-border")
            }`}
            data-testid={testid}
        >
            <div className={`w-9 h-9 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={15} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
                <div className="text-xl font-display font-bold tabular-nums leading-none mt-1">{value}</div>
                {hint && <div className="text-[10px] text-muted-foreground mt-1 truncate">{hint}</div>}
            </div>
        </Component>
    );
}

